import { LocaleText } from '../../../locale';
import React from 'react';
import { marginStyles, mediaPrint } from '../margins';
import { EFontFamily, EMarginSize, EPageOrientation, getFontFamilyCss, getGoogleFontUrl } from '../types';
import { ExportPdfReportChartContainer } from './ExportPdfReportChartContainer';

export type IExportPdfReportLayout = {
  reportText: LocaleText;
  pageOrientation: EPageOrientation;
  marginSize: EMarginSize;
  fontFamily: EFontFamily;
  headerSection: React.ReactElement | null;
  embedTable: React.ReactElement;
  filterSection: React.ReactElement;
  showHeaderInAllPages: boolean;
  reportChartSvg: string;
  footerBottomMargin?: string;
};

export const stringifyStyles = (stylesObject: { [key: string]: string | null }): string => {
  if (stylesObject) {
    return Object.entries(stylesObject)
      .map(([key, value]) => (value ? `${key}: ${value};` : null))
      .filter((style) => !!style)
      .join('\n');
  }
  return '';
};

export const generateMarginStyles = (
  marginSize: EMarginSize,
  showHeaderInAllPages: boolean,
  footerBottomMargin?: string
): JSX.Element => {
  const _marginStylesObject = showHeaderInAllPages ? marginStyles.headerAllPages : marginStyles.headerOnePage;
  const baseMargins =
    marginSize === EMarginSize.normal ? _marginStylesObject.normalMargins : _marginStylesObject.narrowMargins;
  const margins = footerBottomMargin ? { ...baseMargins, 'margin-bottom': footerBottomMargin } : baseMargins;
  const pageStyles = stringifyStyles(margins);
  const mediaPrintStyles = stringifyStyles(mediaPrint);
  return (
    <style data-test="margin-print-styles">
      {`@page {
        ${pageStyles}
      }
       @media print {
          html, body {
          ${mediaPrintStyles}
    }`}
    </style>
  );
};

// Standalone CDN builds extract CSS into <link href*="reporting-app.css"> tags.
// PSP module builds and dev builds inject CSS as <style> tags (via style-loader),
// so we aggregate <style> tag contents in those cases.
const generateCSSElements = (): JSX.Element | JSX.Element[] => {
  if (process.env.NODE_ENV === 'production') {
    const reportingAppCSSSelector = 'link[rel="stylesheet"][href*="reporting-app.css"]';
    const linkElements = Array.from<HTMLLinkElement>(document.querySelectorAll(reportingAppCSSSelector));
    if (linkElements.length > 0) {
      return linkElements.map((link) => <link key={link.href} href={link.href} rel="stylesheet" type="text/css" />);
    }
  }
  const styleElements = Array.from(document.getElementsByTagName('style'));
  const styleText = styleElements.reduce((styles, current) => styles + current.textContent, '');
  return <style>{styleText}</style>;
};

export const ExportPdfReportLayout: React.FC<IExportPdfReportLayout> = ({
  marginSize,
  fontFamily,
  headerSection,
  embedTable,
  filterSection,
  showHeaderInAllPages,
  reportChartSvg,
  footerBottomMargin
}) => {
  const googleFontUrl = getGoogleFontUrl(fontFamily);
  const detailTable = '';

  const reportContent = (
    <>
      <div style={{ ...(headerSection ? { minHeight: '80px' } : {}) }}>
        {headerSection}
        {filterSection}
      </div>
      {reportChartSvg ? (
        <ExportPdfReportChartContainer dataTest="export-report-chart" chartSvg={reportChartSvg} />
      ) : null}
      {detailTable}
      {embedTable}
    </>
  );
  return (
    <html lang="en">
      <head>
        {generateCSSElements()}
        {generateMarginStyles(marginSize, showHeaderInAllPages, footerBottomMargin)}
        {googleFontUrl && <link href={googleFontUrl} rel="stylesheet" />}
      </head>
      <body style={{ fontFamily: getFontFamilyCss(fontFamily) }}>
        {/* PSP module builds prefix every CSS rule with `#reporting-app-container`
            (see `globalCssSelectorPrefix` in reporting-app buildConfig). Without
            this wrapper, none of the SCSS module rules (gridlines, fonts, borders)
            would match in the exported PDF. Harmless for standalone builds. */}
        <div id="reporting-app-container">{reportContent}</div>
      </body>
    </html>
  );
};
